import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, isSubscriptionError } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { downloadReport } from "@/lib/download";
import { formatCurrency } from "@/lib/format";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Save, FileText, FileSpreadsheet, Plus, Trash2, ArrowDownToLine, Printer, AlertTriangle, ZoomIn, ZoomOut } from "lucide-react";

const FUND_KEYS = [
  { key: "cooking_balvatika", label: "Cooking Cost — Bal Vatika", auto: true, hs: false },
  { key: "cooking_primary", label: "Cooking Cost — Primary", auto: true, hs: false },
  { key: "cooking_upper_primary", label: "Cooking Cost — Upper Primary", auto: true, hs: false },
  { key: "cooking_high_school", label: "Cooking Cost — High School", auto: true, hs: true },
  { key: "cch", label: "Cook-cum-Helper Honorarium", auto: false, hs: false },
  { key: "mme", label: "MME / School Expenses", auto: false, hs: false },
];
const FG_GROUPS = [["balvatika", "Bal Vatika", false], ["primary", "Primary", false], ["upper_primary", "Upper Primary", false], ["high_school", "High School", true]];
const PAGE_W = 794; // 210mm @96dpi
const PAGE_H = 2300; // two A4 pages + gap

const num = (v) => (v === "" || v == null || isNaN(parseFloat(v)) ? null : parseFloat(v));
const r2 = (x) => Math.round(x * 100) / 100;
const r3 = (x) => Math.round(x * 1000) / 1000;

function MdcfPreview({ year, month, includeHs, refreshKey }) {
  const { handle402 } = usePlan();
  const [html, setHtml] = useState("");
  const [scale, setScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const wrapRef = useRef(null);
  useEffect(() => {
    api.get("/reports/mdcf/preview-html", { params: { year, month, include_hs: includeHs ? 1 : 0, fill_zero: 0 }, responseType: "text" })
      .then(({ data }) => setHtml(data)).catch((e) => { if (!handle402(e)) toast.error("Preview failed"); });
  }, [year, month, includeHs, refreshKey, handle402]);
  useEffect(() => {
    const fit = () => { if (wrapRef.current) setScale(Math.min(1, wrapRef.current.clientWidth / PAGE_W)); };
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, [html]);
  const s = scale * zoom;
  return (
    <div className="card-thali p-0 overflow-hidden" data-testid="mdcf-preview">
      <div className="px-4 py-2 bg-thali-ink text-white font-heading font-bold text-sm flex items-center justify-between">
        <span>Preview — 2-page MDCF</span>
        <span className="flex items-center gap-2">
          <button onClick={() => setZoom((z) => Math.max(0.5, r2(z - 0.25)))} className="p-1 hover:bg-white/15 rounded" data-testid="mdcf-zoom-out"><ZoomOut className="w-4 h-4" /></button>
          <span className="text-xs font-num w-10 text-center">{Math.round(s * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(2.5, r2(z + 0.25)))} className="p-1 hover:bg-white/15 rounded" data-testid="mdcf-zoom-in"><ZoomIn className="w-4 h-4" /></button>
        </span>
      </div>
      <div ref={wrapRef} className="overflow-auto bg-[#e8e8e8] p-2" style={{ maxHeight: "70vh", touchAction: "pinch-zoom" }}>
        <div style={{ width: PAGE_W * s, height: PAGE_H * s }}>
          {html ? (
            <iframe title="mdcf-preview" srcDoc={html} scrolling="no"
              style={{ width: PAGE_W, height: PAGE_H, border: 0, transform: `scale(${s})`, transformOrigin: "top left" }} />
          ) : <div className="p-6 text-sm text-thali-ink/60">Loading preview…</div>}
        </div>
      </div>
    </div>
  );
}

export default function MDCFForm({ year, month }) {
  const { handle402 } = usePlan();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ school_days: 0, funds: {}, foodgrain: {}, cch_list: [], health: {}, inspection: {}, incidents: "", bank_match: "" });
  const [computed, setComputed] = useState(null);
  const [carry, setCarry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [includeHs, setIncludeHs] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoDays, setAutoDays] = useState(() => localStorage.getItem("pm_mdcf_autodays") !== "0");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/mdcf/meta", { params: { year, month } });
      const m = data.meta || {};
      const wds = data.computed?.totals?.working_days_suggested || 0;
      const school_days = m.school_days || (autoDays ? wds : 0);
      setMeta({ school_days, funds: m.funds || {}, foodgrain: m.foodgrain || {}, cch_list: m.cch_list || [],
                health: m.health || {}, inspection: m.inspection || {}, incidents: m.incidents || "", bank_match: m.bank_match || "" });
      setIncludeHs(m.include_high_school === false ? false : true);
      setComputed(data.computed);
      setCarry(data.carry_forward || null);
    } catch (e) { if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [year, month, autoDays, handle402]);
  useEffect(() => { load(); }, [load]);

  const setFund = (key, field, val) => setMeta((m) => ({ ...m, funds: { ...m.funds, [key]: { ...(m.funds[key] || {}), [field]: val } } }));
  const setFg = (key, field, val) => setMeta((m) => ({ ...m, foodgrain: { ...m.foodgrain, [key]: { ...(m.foodgrain[key] || {}), [field]: val } } }));
  const fgBlur = (key, field) => setMeta((m) => {
    const v = num(m.foodgrain[key]?.[field]);
    if (v == null) return m;
    return { ...m, foodgrain: { ...m.foodgrain, [key]: { ...(m.foodgrain[key] || {}), [field]: v.toFixed(3) } } };
  });
  const setCch = (i, field, val) => setMeta((m) => { const l = [...m.cch_list]; l[i] = { ...l[i], [field]: val }; return { ...m, cch_list: l }; });
  const addCch = () => setMeta((m) => ({ ...m, cch_list: [...m.cch_list, { name: "", gender: "", category: "", payment_mode: "Bank", amount: "" }] }));
  const delCch = (i) => setMeta((m) => ({ ...m, cch_list: m.cch_list.filter((_, x) => x !== i) }));
  const setH = (field, val) => setMeta((m) => ({ ...m, health: { ...m.health, [field]: val } }));
  const setIns = (field, val) => setMeta((m) => ({ ...m, inspection: { ...m.inspection, [field]: val } }));

  const applyCarry = () => {
    if (!carry) return;
    setMeta((m) => {
      const funds = { ...m.funds };
      Object.entries(carry.funds || {}).forEach(([k, v]) => { funds[k] = { ...(funds[k] || {}), opening: v.opening == null ? "" : String(v.opening) }; });
      const foodgrain = { ...m.foodgrain };
      Object.entries(carry.foodgrain || {}).forEach(([k, v]) => { foodgrain[k] = { ...(foodgrain[k] || {}), opening: v.opening == null ? "" : Number(v.opening).toFixed(3) }; });
      return { ...m, funds, foodgrain };
    });
    toast.success(`Opening balances carried forward from ${carry.month_name} ${carry.year}`);
  };

  const toggleAutoDays = (on) => {
    setAutoDays(on); localStorage.setItem("pm_mdcf_autodays", on ? "1" : "0");
    if (on) { const wd = computed?.totals?.working_days_suggested || 0; setMeta((m) => ({ ...m, school_days: wd })); }
  };

  const save = async (silent) => {
    setSaving(true);
    try {
      const { data } = await api.put("/reports/mdcf/meta", { year, month, ...meta, include_high_school: includeHs });
      setComputed(data.computed);
      setRefreshKey((k) => k + 1);
      if (!silent) toast.success("MDCF details saved");
      return true;
    } catch (e) { if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const fundExp = (f, cf) => (f.auto ? cf.expenditure : num(meta.funds[f.key]?.expenditure));
  const closingFund = (f, cf) => {
    const o = num(meta.funds[f.key]?.opening), r = num(meta.funds[f.key]?.received), e = fundExp(f, cf);
    if (o == null || r == null || e == null) return null;
    return r2(o + r - e);
  };
  const closingFg = (key, cons) => {
    const o = num(meta.foodgrain[key]?.opening), r = num(meta.foodgrain[key]?.received);
    if (o == null || r == null) return null;
    return r3(o + r - cons);
  };

  const countEmpty = () => {
    let n = 0;
    FUND_KEYS.filter((f) => includeHs || !f.hs).forEach((f) => {
      const m = meta.funds[f.key] || {};
      if (m.opening === "" || m.opening == null || m.received === "" || m.received == null) n += 1;
    });
    FG_GROUPS.filter(([, , hs]) => includeHs || !hs).forEach(([k]) => {
      const m = meta.foodgrain[k] || {};
      if (m.opening === "" || m.opening == null || m.received === "" || m.received == null) n += 1;
    });
    return n;
  };

  const udise = (computed?.school || {}).udise || "NA";
  const fname = `MDCF_${udise}_${String(month).padStart(2, "0")}-${year}`;

  const openPrint = async (toastMsg) => {
    const { data } = await api.get("/reports/mdcf/preview-html", { params: { year, month, include_hs: includeHs ? 1 : 0, fill_zero: 1 }, responseType: "text" });
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups, then tap Print again."); return false; }
    w.document.open(); w.document.write(data); w.document.close();
    w.focus(); setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 500);
    if (toastMsg) toast.message(toastMsg);
    return true;
  };

  const dlPdf = async () => {
    const empty = countEmpty();
    if (empty > 0 && !window.confirm(`Opening/Received are empty for ${empty} row(s). Use 0?`)) return;
    setBusy("pdf");
    try {
      if (!(await save(true))) return;
      const res = await api.get("/reports/mdcf/export", { params: { year, month, fmt: "pdf", include_hs: includeHs ? 1 : 0, fill_zero: 1 }, responseType: "blob" });
      const ct = (res.headers?.["content-type"] || "").toLowerCase();
      if (!ct.includes("application/pdf")) throw new Error("Server did not return a PDF");
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);
      if (isIOS) {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a"); a.href = url; a.download = `${fname}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
      toast.success("PDF downloaded");
    } catch (e) {
      if (isSubscriptionError(e)) { handle402(e); return; }
      if (e.response?.status === 401) return; // interceptor redirects to login
      let reason = "";
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          reason = formatApiError(JSON.parse(txt).detail);
        } else if (e.response?.data?.detail) {
          reason = formatApiError(e.response.data.detail);
        }
      } catch { /* noop */ }
      toast.error(reason || "Couldn't generate the PDF on the server. Opening print view — tap Print → Save as PDF.");
      await openPrint("Tap Print → Save as PDF (A4, 100%, no margins).");
    } finally { setBusy(""); }
  };
  const dlExcel = async () => {
    setBusy("excel");
    try { if (!(await save(true))) return; await downloadReport("mdcf", "excel", { year, month, include_hs: includeHs ? 1 : 0 }, fname); toast.success("Excel downloaded"); }
    catch (e) { if (isSubscriptionError(e)) { handle402(e); return; } if (e.response?.status !== 401) toast.error("Couldn't generate the Excel file. Please try again."); }
    finally { setBusy(""); }
  };
  const doPrint = async () => {
    setBusy("print");
    try { if (!(await save(true))) return; await openPrint(); }
    catch (e) { if (!handle402(e)) toast.error("Print failed"); }
    finally { setBusy(""); }
  };

  if (loading || !computed) return <Loader />;
  const c = computed;
  const funds = FUND_KEYS.filter((f) => includeHs || !f.hs);
  const fgGroups = FG_GROUPS.filter(([, , hs]) => includeHs || !hs);
  const ClosingCell = ({ val, money }) => {
    if (val == null) return <span className="text-thali-ink/40" title="Enter opening & received">—</span>;
    const txt = money ? formatCurrency(val) : val.toFixed(3);
    return <span className="font-semibold" style={{ color: val < 0 ? "#dc2626" : "#0B6B3A" }}>{txt}</span>;
  };

  return (
    <div className="space-y-4 pb-28" data-testid="mdcf-form">
      <div className="rounded-card bg-thali-green text-white p-4">
        <div className="font-heading font-bold text-lg">Official MDCF — {c.month_name} {c.year}</div>
        <div className="text-sm opacity-80">School details come from your Profile. Enter the monthly balances below, Save, then download the 2-page form.</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {carry && (
            <button onClick={applyCarry} className="bg-white/20 hover:bg-white/30 rounded-xl px-4 min-h-[44px] text-sm font-bold inline-flex items-center gap-2 transition-colors" data-testid="mdcf-carry">
              <ArrowDownToLine className="w-4 h-4" /> Carry forward from {carry.month_name} {carry.year}
            </button>
          )}
          <label className="bg-white/20 rounded-xl px-4 min-h-[44px] text-sm font-bold inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeHs} onChange={(e) => setIncludeHs(e.target.checked)} className="w-4 h-4 accent-white" data-testid="mdcf-include-hs" />
            Include High School
          </label>
        </div>
      </div>

      {c.totals?.dry_days > 0 && (
        <div className="card-thali bg-thali-turmeric/15 border border-thali-turmericdark/30" data-testid="mdcf-dry-note">
          <div className="font-heading font-bold text-sm">🌾 Dry Ration</div>
          <div className="text-sm text-thali-ink/70 mt-1">{c.totals.dry_days} dry-ration day(s). Rice consumption below <b>includes</b> dry-ration rice.</div>
        </div>
      )}

      {/* Meals (auto) */}
      <div className="card-thali p-0 overflow-x-auto">
        <div className="px-4 py-2 bg-thali-green/90 text-white font-heading font-bold text-sm">1. Meals Availed (auto)</div>
        <table className="w-full text-sm min-w-[460px]">
          <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Group</th><th className="p-2">Enrol.</th><th className="p-2">School Days</th><th className="p-2">MDM Days</th><th className="p-2 pr-4">Meals</th></tr></thead>
          <tbody>{c.meals.filter((r) => includeHs || r.key !== "high_school").map((r) => <tr key={r.group} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4">{r.group}</td><td className="p-2 text-center">{r.enrolment}</td><td className="p-2 text-center">{meta.school_days}</td><td className="p-2 text-center">{r.mdm_days}</td><td className="p-2 pr-4 text-center">{r.meals_served}</td></tr>)}</tbody>
        </table>
        <div className="px-4 py-2 flex items-center justify-between bg-thali-cream text-sm gap-2 flex-wrap">
          <label className="flex items-center gap-2 font-semibold cursor-pointer">
            <input type="checkbox" checked={autoDays} onChange={(e) => toggleAutoDays(e.target.checked)} className="w-4 h-4 accent-thali-green" data-testid="mdcf-auto-days" />
            Auto school days from holidays
          </label>
          <div className="flex items-center gap-2">
            <span className="font-semibold">School Days</span>
            <input inputMode="numeric" className="w-20 h-9 text-center rounded-lg border border-thali-turmeric/40" value={meta.school_days}
              onChange={(e) => setMeta({ ...meta, school_days: parseInt(e.target.value || 0, 10) || 0 })} data-testid="mdcf-school-days" />
          </div>
        </div>
      </div>

      {/* Fund details */}
      <div className="card-thali p-0 overflow-x-auto">
        <div className="px-4 py-2 bg-thali-green/90 text-white font-heading font-bold text-sm">2. Fund Details (Rs.)</div>
        <table className="w-full text-xs min-w-[560px]">
          <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Component</th><th className="p-2">Opening</th><th className="p-2">Received</th><th className="p-2">Expenditure</th><th className="p-2 pr-4">Closing</th></tr></thead>
          <tbody>
            {funds.map((f) => {
              const cf = c.funds[FUND_KEYS.indexOf(f)];
              const m = meta.funds[f.key] || {};
              return (
                <tr key={f.key} className="border-t border-thali-turmeric/10">
                  <td className="p-2 pl-4">{f.label}</td>
                  <td className="p-1"><input inputMode="decimal" className="w-20 h-9 text-center rounded border border-thali-turmeric/40" value={m.opening ?? ""} onChange={(e) => setFund(f.key, "opening", e.target.value)} data-testid={`mdcf-fund-${f.key}-opening`} /></td>
                  <td className="p-1"><input inputMode="decimal" className="w-20 h-9 text-center rounded border border-thali-turmeric/40" value={m.received ?? ""} onChange={(e) => setFund(f.key, "received", e.target.value)} data-testid={`mdcf-fund-${f.key}-received`} /></td>
                  <td className="p-1 text-center">
                    {f.auto ? <span className="font-semibold">{formatCurrency(cf.expenditure || 0)}</span>
                      : <input inputMode="decimal" className="w-20 h-9 text-center rounded border border-thali-turmeric/40" value={m.expenditure ?? ""} onChange={(e) => setFund(f.key, "expenditure", e.target.value)} data-testid={`mdcf-fund-${f.key}-exp`} />}
                  </td>
                  <td className="p-2 pr-4 text-center" data-testid={`mdcf-fund-${f.key}-closing`}><ClosingCell val={closingFund(f, cf)} money /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-4 py-2 bg-thali-cream text-sm flex items-center gap-3 flex-wrap">
          <span className="font-semibold">Closing matches bank balance?</span>
          {["yes", "no"].map((v) => (
            <label key={v} className="flex items-center gap-1 cursor-pointer"><input type="radio" name="bankmatch" checked={meta.bank_match === v} onChange={() => setMeta({ ...meta, bank_match: v })} className="accent-thali-green" data-testid={`mdcf-bank-${v}`} /> {v === "yes" ? "Yes" : "No"}</label>
          ))}
        </div>
      </div>

      {/* Food grains */}
      <div className="card-thali p-0 overflow-x-auto">
        <div className="px-4 py-2 bg-thali-green/90 text-white font-heading font-bold text-sm">3. Food Grains — Rice (kg, 3 decimals)</div>
        <table className="w-full text-xs min-w-[560px]">
          <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Group</th><th className="p-2">Opening</th><th className="p-2">Received</th><th className="p-2">Consumption</th><th className="p-2 pr-4">Closing</th></tr></thead>
          <tbody>
            {fgGroups.map(([key, label]) => {
              const idx = FG_GROUPS.findIndex((g) => g[0] === key);
              const cf = c.foodgrain[idx];
              const m = meta.foodgrain[key] || {};
              return (
                <tr key={key} className="border-t border-thali-turmeric/10">
                  <td className="p-2 pl-4">{label}</td>
                  <td className="p-1"><input inputMode="decimal" className="w-24 h-9 text-center rounded border border-thali-turmeric/40" value={m.opening ?? ""} onChange={(e) => setFg(key, "opening", e.target.value)} onBlur={() => fgBlur(key, "opening")} data-testid={`mdcf-fg-${key}-opening`} /></td>
                  <td className="p-1"><input inputMode="decimal" className="w-24 h-9 text-center rounded border border-thali-turmeric/40" value={m.received ?? ""} onChange={(e) => setFg(key, "received", e.target.value)} onBlur={() => fgBlur(key, "received")} data-testid={`mdcf-fg-${key}-received`} /></td>
                  <td className="p-2 text-center font-semibold" data-testid={`mdcf-fg-${key}-consumption`}>{cf.consumption.toFixed(3)}{cf.dry_consumption > 0 && <div className="text-[10px] font-normal text-thali-ink/50">incl. dry {cf.dry_consumption.toFixed(3)} kg</div>}</td>
                  <td className="p-2 pr-4 text-center" data-testid={`mdcf-fg-${key}-closing`}><ClosingCell val={closingFg(key, cf.consumption)} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CCH */}
      <div className="card-thali">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-bold text-sm">4. Cook-cum-Helper Details</div>
          <button onClick={addCch} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid="mdcf-add-cch"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="space-y-2">
          {meta.cch_list.map((cc, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-center" data-testid={`mdcf-cch-${i}`}>
              <input className="input-thali h-10 sm:col-span-2" placeholder="Name" value={cc.name || ""} onChange={(e) => setCch(i, "name", e.target.value)} />
              <input className="input-thali h-10" placeholder="M/F" value={cc.gender || ""} onChange={(e) => setCch(i, "gender", e.target.value)} />
              <input className="input-thali h-10" placeholder="Category" value={cc.category || ""} onChange={(e) => setCch(i, "category", e.target.value)} />
              <input className="input-thali h-10" placeholder="Amount" inputMode="decimal" value={cc.amount || ""} onChange={(e) => setCch(i, "amount", e.target.value)} />
              <button onClick={() => delCch(i)} className="w-10 h-10 rounded-lg bg-red-50 text-red-600 grid place-items-center justify-self-start"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {meta.cch_list.length === 0 && <p className="text-sm text-thali-ink/50">No cook-cum-helper added.</p>}
        </div>
      </div>

      {/* Health + Inspection + Incidents */}
      <div className="card-thali grid sm:grid-cols-2 gap-3">
        <div>
          <div className="font-heading font-bold text-sm mb-2">6. Children Health Status</div>
          {[["ifa_boys", "IFA tablets (Boys)"], ["ifa_girls", "IFA tablets (Girls)"], ["screened", "Screened by RBSK"], ["referred", "Referred by RBSK"]].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs">{l}</span>
              <input inputMode="numeric" className="w-20 h-8 text-center rounded border border-thali-turmeric/40" value={meta.health[k] ?? ""} onChange={(e) => setH(k, e.target.value)} data-testid={`mdcf-health-${k}`} />
            </div>
          ))}
        </div>
        <div>
          <div className="font-heading font-bold text-sm mb-2">7. School Inspection</div>
          <div className="flex items-center gap-3 mb-2 text-xs">
            <span className="font-semibold">Done this month?</span>
            {["yes", "no"].map((v) => <label key={v} className="flex items-center gap-1 cursor-pointer"><input type="radio" name="insdone" checked={meta.inspection.done === v} onChange={() => setIns("done", v)} className="accent-thali-green" data-testid={`mdcf-ins-${v}`} /> {v === "yes" ? "Yes" : "No"}</label>)}
          </div>
          {[["task_force", "By Task Force"], ["district", "By District Officials"], ["block", "By Block Officials"], ["smc", "By SMC Members"]].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs">{l}</span>
              <input className="w-28 h-8 text-center rounded border border-thali-turmeric/40 text-xs" value={meta.inspection[k] ?? ""} onChange={(e) => setIns(k, e.target.value)} data-testid={`mdcf-ins-${k}`} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-xs font-semibold">Untoward Incidents</span>
            <input className="w-20 h-8 text-center rounded border border-thali-turmeric/40" value={meta.incidents ?? ""} onChange={(e) => setMeta({ ...meta, incidents: e.target.value })} data-testid="mdcf-incidents" />
          </div>
        </div>
      </div>

      {countEmpty() > 0 && (
        <div className="card-thali bg-amber-50 border border-amber-300 flex items-center gap-2 text-sm text-amber-800" data-testid="mdcf-empty-warn">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {countEmpty()} row(s) have empty Opening/Received — closing shows “—”. You'll be asked to use 0 when downloading the PDF.
        </div>
      )}

      <button onClick={() => save(false)} disabled={saving} className="btn-3d-green w-full" data-testid="mdcf-save"><Save className="w-5 h-5" /> {saving ? "…" : "Save MDCF Details"}</button>

      <MdcfPreview year={year} month={month} includeHs={includeHs} refreshKey={refreshKey} />

      <div className="fixed bottom-[60px] md:bottom-0 inset-x-0 md:left-64 bg-thali-paper border-t border-thali-turmeric/20 p-3 flex gap-2 z-50 justify-center shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <button onClick={dlPdf} disabled={!!busy} className="btn-3d-saffron flex-1 max-w-[190px]" data-testid="mdcf-pdf"><FileText className="w-4 h-4" /> {busy === "pdf" ? "Preparing PDF…" : "PDF"}</button>
        <button onClick={doPrint} disabled={!!busy} className="btn-ghost flex-1 max-w-[150px]" data-testid="mdcf-print"><Printer className="w-4 h-4" /> {busy === "print" ? "…" : "Print"}</button>
        <button onClick={dlExcel} disabled={!!busy} className="btn-3d-green flex-1 max-w-[150px]" data-testid="mdcf-excel"><FileSpreadsheet className="w-4 h-4" /> {busy === "excel" ? "…" : "Excel"}</button>
      </div>
    </div>
  );
}
